import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Collapse, Spin } from "antd";
import { DoubleLeftOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";
import {
	buildKycCompanyTabUrl,
	buildKycMaterialDesignTabReturn,
	buildKycMaterialsTableSearchReturn,
	buildKycTableSearchUrl,
	buildKycTileCountsPayloadWithScope,
	getQuotationTileCounts,
	loadKycDesignNavContext,
	saveKycDesignNavContext,
	getKycAppliedFilterPayload,
} from "./kycHelpers";
import {
	DesignSectionFieldGrid,
	KYC_DEFAULT_FILTER_PAYLOAD,
	parseDesignDetailsListResponse,
} from "./kycDesignShared";
import "./KYC-Dashboard.css";
import KycStickyTopBar from "./KycStickyTopBar";
import { KYCQuotationImage } from "../Elite/imageAssets";

const { Panel } = Collapse;

const getFirstAvailableValue = (...values) =>
	values.find((value) => value !== undefined && value !== null && value !== "");

const hasDisplayCount = (value) => {
	if (value === undefined || value === null || value === "") {
		return false;
	}
	const numericValue = Number(value);
	if (!Number.isNaN(numericValue) && numericValue === 0) {
		return false;
	}
	return true;
};

const getCardTitle = (countObj, label, keys = ["totalCount", "total"]) => {
	const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
	return hasDisplayCount(countValue) ? `${countValue} ${label}` : `- ${label}`;
};

const getCardCountValue = (countObj, keys = []) => {
	if (!countObj || Object.keys(countObj).length === 0) {
		return "-";
	}
	const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
	return hasDisplayCount(countValue) ? countValue : "-";
};

const buildDesignDetailsListPayload = (generalDetailId) => ({
	pageindex: 1,
	pagesize: 10,
	sortcolumn: "",
	orderBy: "",
	search: "",
	...KYC_DEFAULT_FILTER_PAYLOAD,
	...getKycAppliedFilterPayload(),
	generalDetailId: Number(generalDetailId) || generalDetailId,
});

const KycDesignDetails = () => {
	const { generalDetailId } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [isTilesLoading, setIsTilesLoading] = useState(false);
	const [sectionList, setSectionList] = useState([]);
	const [activePanels, setActivePanels] = useState([]);
	const [quotationCount, setQuotationCount] = useState({});

	const resolvedGeneralDetailId = useMemo(() => {
		const fromParam = generalDetailId ? String(generalDetailId).trim() : "";
		if (fromParam) {
			return fromParam;
		}
		return location.state?.generalDetailId ? String(location.state.generalDetailId).trim() : "";
	}, [generalDetailId, location.state?.generalDetailId]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [resolvedGeneralDetailId]);

	const navContext = useMemo(() => {
		const fromState = location.state || {};
		const stored = loadKycDesignNavContext(resolvedGeneralDetailId);
		return {
			designMasterNumber:
				fromState.designMasterNumber || stored.designMasterNumber || resolvedGeneralDetailId || "Design",
			groupCompanyId: fromState.groupCompanyId || stored.groupCompanyId || "",
			groupCompanyName: fromState.groupCompanyName || stored.groupCompanyName || "",
			customerName: fromState.customerName || stored.customerName || "",
			material: fromState.material || stored.material || "",
			returnSource: fromState.returnSource || stored.returnSource || "dashboard",
			materialProductName: fromState.materialProductName || stored.materialProductName || "",
			materialName: fromState.materialName || stored.materialName || "",
			materialsCustomer: fromState.materialsCustomer || stored.materialsCustomer || "",
			accountName: fromState.accountName || stored.accountName || "",
			returnTab: fromState.returnTab || stored.returnTab || "design",
		};
	}, [location.state, resolvedGeneralDetailId]);

	const isMaterialReturn =
		navContext.returnSource === "material" && Boolean(navContext.materialProductName);

	useEffect(() => {
		if (!resolvedGeneralDetailId) {
			return;
		}

		saveKycDesignNavContext(resolvedGeneralDetailId, {
			designMasterNumber: navContext.designMasterNumber,
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: navContext.groupCompanyName,
			customerName: navContext.customerName,
			material: navContext.material,
			returnSource: navContext.returnSource,
			materialProductName: navContext.materialProductName,
			materialName: navContext.materialName,
			materialsCustomer: navContext.materialsCustomer,
			accountName: navContext.accountName,
			returnTab: navContext.returnTab,
		});
	}, [resolvedGeneralDetailId, navContext]);

	const dashboardDesignTabLink = buildKycCompanyTabUrl("design");

	useEffect(() => {
		if (!resolvedGeneralDetailId) {
			return undefined;
		}

		setIsLoading(true);
		const requestData = buildDesignDetailsListPayload(resolvedGeneralDetailId);

		KycService.getKycDesignDetailsListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const sections = parseDesignDetailsListResponse(response);
					setSectionList(sections);
					setActivePanels(sections.map((section) => section.groupKey));
				} else {
					NotificationManager.error(response.data?.message || "Unable to load design details.");
					setSectionList([]);
					setActivePanels([]);
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load design details.");
				setSectionList([]);
				setActivePanels([]);
			})
			.finally(() => {
				setIsLoading(false);
			});

		return undefined;
	}, [resolvedGeneralDetailId]);

	useEffect(() => {
		if (!resolvedGeneralDetailId) {
			return undefined;
		}

		setIsTilesLoading(true);
		KycService.getKycTileCountsAPI(
			buildKycTileCountsPayloadWithScope(buildDesignDetailsListPayload(resolvedGeneralDetailId), {
				generalDetailId: resolvedGeneralDetailId,
			})
		)
			.then((response) => {
				if (response.data?.statusCode === 200) {
					const apiData = response.data.responseData || {};
					setQuotationCount(getQuotationTileCounts(apiData));
				}
			})
			.catch((error) => {
				console.log(error, "Error");
			})
			.finally(() => {
				setIsTilesLoading(false);
			});

		return undefined;
	}, [resolvedGeneralDetailId]);

	const handleCollapseChange = (keys) => {
		setActivePanels(Array.isArray(keys) ? keys : keys ? [keys] : []);
	};

	const designBreadcrumbLabel = useMemo(() => {
		return navContext.designMasterNumber || "Design";
	}, [navContext.designMasterNumber, sectionList]);

	const materialDesignTabReturn = useMemo(
		() =>
			buildKycMaterialDesignTabReturn({
				productName: navContext.materialProductName,
				materialName: navContext.materialName || navContext.materialProductName,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: navContext.accountName,
				returnTab: navContext.returnTab,
			}),
		[navContext]
	);

	const dashboardMaterialsReturn = useMemo(
		() =>
			buildKycMaterialsTableSearchReturn({
				materialName: navContext.materialName || navContext.materialProductName,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: navContext.accountName,
			}),
		[navContext]
	);

	const dashboardDesignBackLink = useMemo(() => {
		if (isMaterialReturn) {
			return materialDesignTabReturn.link;
		}
		const search = designBreadcrumbLabel !== "Design" ? String(designBreadcrumbLabel).trim() : "";
		if (search) {
			return buildKycTableSearchUrl(search, { companyTab: "design" });
		}
		return buildKycCompanyTabUrl("design");
	}, [isMaterialReturn, materialDesignTabReturn.link, designBreadcrumbLabel]);

	const dashboardDesignBackState = useMemo(() => {
		if (isMaterialReturn) {
			return materialDesignTabReturn.state;
		}
		const search = designBreadcrumbLabel !== "Design" ? String(designBreadcrumbLabel).trim() : "";
		if (!search) {
			return { returnTab: "design" };
		}
		return {
			returnTab: "design",
			designMasterNumber: search,
			tableSearch: search,
		};
	}, [isMaterialReturn, materialDesignTabReturn.state, designBreadcrumbLabel]);

	const materialBreadcrumbLabel =
		navContext.materialName || navContext.materialProductName || navContext.material || "Material";

	return (
		<Fragment>
			<div className="dashboard app-content content kyc-design-details-page">
				<div className="content-wrapper">
					<div className="content-body">
						<KycStickyTopBar
							variant="details"
							className="kyc-dashboard-top-bar details_view_title kyc-details-top-bar kyc-design-details-topbar">
							<div className="kyc-dashboard-top-bar__left">
								<div className="kyc-dashboard-top-bar__breadcrumb">
									<Breadcrumb className="details_breadcrum" separator={">"}>
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								{isMaterialReturn ? (
									<>
										<Breadcrumb.Item>
											<Link
												to={dashboardMaterialsReturn.link}
												state={dashboardMaterialsReturn.state}>
												<label>{materialBreadcrumbLabel}</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<Link to={dashboardDesignBackLink} state={dashboardDesignBackState}>
												<label>Design</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{designBreadcrumbLabel}</label>
										</Breadcrumb.Item>
									</>
								) : (
									<>
										<Breadcrumb.Item>
											<Link to={dashboardDesignTabLink}>
												<label>Design</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{designBreadcrumbLabel}</label>
										</Breadcrumb.Item>
									</>
								)}
							</Breadcrumb>
								</div>
							</div>
							<div className="kyc-dashboard-top-bar__actions">
							<Link to={dashboardDesignBackLink} state={dashboardDesignBackState}>
								<Button type="primary" className="back_btn" icon={<DoubleLeftOutlined />}>
									Back
								</Button>
							</Link>
							</div>
						</KycStickyTopBar>

						<h4 className="kyc-design-details-page-title">View Design Details</h4>

						<Spin spinning={isTilesLoading} wrapperClassName="kyc-mc-tiles-spin">
							<div className="row mb-3">
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_blue kyc-design-detail-tile">
										<div className="card-content">
											<div className="kyc-card-body KYCQuotation images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCQuotationImage} alt="Img" />
														<span>{getCardTitle(quotationCount, "Quotation")}</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getCardCountValue(quotationCount, ["runningCount", "running"])}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3" />
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getCardCountValue(quotationCount, ["hiddenCount", "hide"])}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</Spin>

						<Spin spinning={isLoading}>
							<div className="kyc-design-accordions-wrap">
								{sectionList.length === 0 && !isLoading ? (
									<div className="kyc-tab-placeholder">No design details found.</div>
								) : (
									<Collapse
										activeKey={activePanels}
										onChange={handleCollapseChange}
										className="kyc-mc-collapse kyc-design-sections-collapse">
										{sectionList.map((section) => {
											const isPanelOpen = activePanels.includes(section.groupKey);
											return (
												<Panel
													showArrow={false}
													key={section.groupKey}
													header={
														<span className="kyc-design-panel-header">
															<span className="kyc-design-panel-title">{section.sectionName}</span>
															<span
																className={`kyc-design-expand-icon ${
																	isPanelOpen ? "kyc-design-expand-icon--active" : ""
																}`}>
																{isPanelOpen ? <MinusOutlined /> : <PlusOutlined />}
															</span>
														</span>
													}>
													<h6 className="kyc-design-accordion-section-title">{section.sectionName}</h6>
													<DesignSectionFieldGrid sectionData={section.sectionData} />
												</Panel>
											);
										})}
									</Collapse>
								)}
							</div>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycDesignDetails;
